import { useState } from "react";
import CopyCode from "../../CopyCode/CopyCode";
import Icon from "../../Icon";

const WarningCard = () => {
  const [dismissed, setDismissed] = useState({});

  const toggleDismiss = (id) => {
    setDismissed(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  return (
    <div className="flex flex-col gap-y-10 p-6">
      {/* SECTION 1: WARNING TYPES */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
        <h2 className="text-xl font-bold text-white mb-6">Warning Types</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* Variation 1: Basic Warning */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Basic Warning</label>
            <div className="p-4 bg-amber-900/20 border border-amber-700/50 rounded-lg flex items-start gap-3">
              <span className="text-2xl flex-shrink-0"><Icon name="warning" className="w-6 h-6" /></span>
              <div>
                <h4 className="font-semibold text-amber-300">Alert</h4>
                <p className="text-sm text-amber-200 mt-1">This is an important warning message for the user.</p>
              </div>
            </div>
            <CopyCode number_code={54} code={`<div className="p-4 bg-amber-900/20 
  border border-amber-700/50 rounded-lg flex gap-3">
  <span className="text-2xl">Icon</span>
  <div>
    <h4 className="font-semibold text-amber-300">
      Alert
    </h4>
    <p className="text-sm text-amber-200 mt-1">
      Important warning message.
    </p>
  </div>
</div>`} />
          </div>

          {/* Variation 2: Deprecation Notice */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Deprecation Notice</label>
            <div className="p-4 bg-amber-900/20 border border-amber-700/50 rounded-lg">
              <h4 className="font-semibold text-amber-300 mb-2">Deprecated API</h4>
              <p className="text-sm text-amber-200 mb-3">The <code className="bg-slate-800 px-1 rounded text-xs">getUser()</code> method is deprecated. Use <code className="bg-slate-800 px-1 rounded text-xs">fetchUser()</code> instead.</p>
              <a href="#" className="text-xs text-amber-400 hover:text-amber-300 inline-flex items-center gap-1"><Icon name="arrowRight" className="w-3 h-3" /> Migration Guide</a>
            </div>
            <CopyCode number_code={55} code={`<div className="p-4 bg-amber-900/20 
  border border-amber-700/50 rounded">
  <h4 className="font-semibold text-amber-300">
    Deprecated API
  </h4>
  <p className="text-sm text-amber-200 mt-2">
    getUser() is deprecated. Use fetchUser() 
    instead.
  </p>
  <a className="text-xs text-amber-400 mt-3">
    Migration Guide
  </a>
</div>`} />
          </div>

          {/* Variation 3: Maintenance Notice */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Maintenance Notice</label>
            <div className="p-4 bg-slate-700/40 border border-slate-600/50 rounded-lg flex items-start gap-3">
              <span className="text-2xl flex-shrink-0">M</span>
              <div>
                <h4 className="font-semibold text-slate-200">Scheduled Maintenance</h4>
                <p className="text-sm text-slate-400 mt-1">Scheduled maintenance: March 20 2:00 AM - 4:00 AM UTC</p>
              </div>
            </div>
            <CopyCode number_code={56} code={`<div className="p-4 bg-slate-700/40 
  border border-slate-600/50 rounded-lg flex gap-3">
  <span className="text-2xl">Maintenance</span>
  <div>
    <h4 className="font-semibold text-slate-200">
      Scheduled Maintenance
    </h4>
    <p className="text-slate-400 text-sm mt-1">
      March 20 2:00 AM - 4:00 AM UTC
    </p>
  </div>
</div>`} />
          </div>

          {/* Variation 4: Quota Warning */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Quota Warning</label>
            <div className="p-4 bg-orange-900/20 border border-orange-700/50 rounded-lg">
              <h4 className="font-semibold text-orange-300 mb-2">API Rate Limit</h4>
              <p className="text-sm text-orange-200 mb-3">You've used 950 of 1000 API calls this month.</p>
              <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
                <div className="h-full bg-orange-500 w-95/100"></div>
              </div>
              <p className="text-xs text-orange-300 mt-2">95% used - Resets in 8 days</p>
            </div>
            <CopyCode number_code={57} code={`<div className="p-4 bg-orange-900/20 rounded">
  <h4 className="font-semibold text-orange-300">
    API Rate Limit
  </h4>
  <p className="text-sm text-orange-200 mt-2">
    950 of 1000 calls used this month.
  </p>
  <div className="w-full h-2 bg-slate-700 
    rounded-full overflow-hidden mt-3">
    <div className="h-full bg-orange-500 w-95/100">
    </div>
  </div>
</div>`} />
          </div>

        </div>
      </div>

      {/* SECTION 2: WARNING ACTIONS */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
        <h2 className="text-xl font-bold text-amber-400 mb-6">Warning Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* Variation 5: Dismissible Warning */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Dismissible Warning</label>
            {!dismissed['warn1'] && (
              <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg flex items-start gap-3">
                <span className="text-lg flex-shrink-0"><Icon name="info" className="w-4 h-4" /></span>
                <div className="flex-1">
                  <h4 className="font-semibold text-blue-300">Browser Update</h4>
                  <p className="text-sm text-blue-200 mt-1">Your browser is outdated. Update for better performance.</p>
                </div>
                <button onClick={() => toggleDismiss('warn1')} className="text-slate-400 hover:text-slate-200"><Icon name="close" className="w-4 h-4" /></button>
              </div>
            )}
            {dismissed['warn1'] && <p className="text-xs text-slate-400 p-2">Dismissed</p>}
            <CopyCode number_code={58} code={`{!dismissed && (
  <div className="p-4 bg-blue-900/20 rounded-lg 
    flex items-start gap-3">
    <span className="text-lg">Icon</span>
    <div className="flex-1">
      <h4 className="font-semibold ">
        Browser Update
      </h4>
      <p className="text-sm mt-1">
        Update for better performance.
      </p>
    </div>
    <button onClick={() => setDismissed(true)}>
      Close
    </button>
  </div>
)}`} />
          </div>

          {/* Variation 6: With Action */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Warning with Action</label>
            <div className="p-4 bg-red-900/20 border border-red-700/50 rounded-lg">
              <h4 className="font-semibold text-red-300 mb-2">Payment Method Expiring</h4>
              <p className="text-sm text-red-200 mb-4">Your credit card ending in 4242 expires March 2025.</p>
              <button className="w-full px-3 py-2 bg-red-600 hover:bg-red-500 text-sm font-semibold text-white rounded">Update Payment Method</button>
            </div>
            <CopyCode number_code={59} code={`<div className="p-4 bg-red-900/20 rounded">
  <h4 className="font-semibold text-red-300">
    Payment Expiring
  </h4>
  <p className="text-sm text-red-200 mt-2 mb-4">
    Your card expires March 2025.
  </p>
  <button className="w-full px-3 py-2 
    bg-red-600 text-white rounded">
    Update Payment
  </button>
</div>`} />
          </div>

          {/* Variation 7: Critical Warning */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Critical Warning</label>
            <div className="p-4 bg-red-900/30 border-2 border-red-600/80 rounded-lg animate-pulse">
              <h4 className="font-bold text-red-300 mb-2 inline-flex items-center gap-2"><Icon name="alert" className="w-4 h-4" /> CRITICAL SECURITY ALERT</h4>
              <p className="text-sm text-red-200 mb-3">Unauthorized access detected from unknown IP. Secure your account immediately.</p>
              <button className="w-full px-3 py-2 bg-red-700 hover:bg-red-600 text-sm font-semibold text-white rounded">Secure Account Now</button>
            </div>
            <CopyCode number_code={60} code={`<div className="p-4 bg-red-900/30 border-2 
  border-red-600/80 rounded-lg animate-pulse">
  <h4 className="font-bold text-red-300">
    CRITICAL ALERT
  </h4>
  <p className="text-sm text-red-200 mt-2 mb-3">
    Unauthorized access detected.
  </p>
  <button className="w-full bg-red-700 
    text-white rounded">
    Secure Now
  </button>
</div>`} />
          </div>

          {/* Variation 8: Urgent Warning */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Urgent Warning</label>
            <div className="p-4 bg-amber-900/25 border border-amber-700/60 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
                <h4 className="font-bold text-amber-300 inline-flex items-center gap-2"><Icon name="timer" className="w-4 h-4" /> Urgent Action Required</h4>
              </div>
              <p className="text-sm text-amber-200 mb-4">Your subscription expires in 1 day. Renew now to avoid service interruption.</p>
              <button className="w-full px-3 py-2 bg-amber-600 hover:bg-amber-500 text-sm font-semibold text-white rounded">Renew Subscription</button>
            </div>
            <CopyCode number_code={61} code={`<div className="p-4 bg-amber-900/25 
  border border-amber-700/60 rounded">
  <div className="flex items-center gap-2 mb-2">
    <span className="w-2 h-2 bg-amber-500 
      rounded-full animate-pulse"></span>
    <h4 className="font-bold text-amber-300">
      Urgent Action
    </h4>
  </div>
  <button className="w-full bg-amber-600 
    text-white rounded">
    Renew Now
  </button>
</div>`} />
          </div>

        </div>
      </div>
    </div>
  );
};

export default WarningCard;


